import { Fragment, memo } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { NEGOCIATED_TYPES } from "app/constants";
import get from "lodash/get";
import "./NegociatedItem.scss";
import IconNegoTrain from "app/pages/.shared/IconNegoTrain";
import { useIntl } from "react-intl";
import LineClamp from "app/pages/.shared/LineClamp/LineClamp";
import { htmlSanitize } from "app/utils/sanitize";
import TransferFilledIcon from "app/icons/navigation/TransferFilledIcon";
import MealFilledIcon from "app/icons/travel/MealFilledIcon";
import AirplaneFilledIcon from "app/icons/travel/AirplaneFilledIcon";
import TourFilledIcon from "app/icons/travel/TourFilledIcon";
import BedFilledIcon from "app/icons/travel/BedFilledIcon";
import CheckIcon from "app/icons/navigation/CheckIcon";
import { ICON_SIZES } from "app/constants";

const NEGOTIATED_ICONS = sizeIcon => {
	const iconProps = sizeIcon ? { size: sizeIcon } : {};

	return {
		[NEGOCIATED_TYPES.ACCOMMODATION]: <BedFilledIcon {...iconProps} />,
		[NEGOCIATED_TYPES.FLIGHT]: <AirplaneFilledIcon {...iconProps} />,
		[NEGOCIATED_TYPES.TRANSFER]: <TransferFilledIcon {...iconProps} />,
		[NEGOCIATED_TYPES.BOARD]: <MealFilledIcon {...iconProps} />,
		[NEGOCIATED_TYPES.TRAIN]: <IconNegoTrain {...iconProps} />,
		[NEGOCIATED_TYPES.TRANSPORTATION]: <TourFilledIcon {...iconProps} />,
	};
};

const NegociatedItem = ({
	type,
	label,
	enableLineClamp,
	lineClampFullText,
	isTransportOnly,
	specialOffer,
	offerWithTransportLabel,
	labelPrefix,
	sizeIcon,
}) => {
	const specialOfferTitleColor = get(specialOffer, "ui.titleColor");
	const specialOfferPictoUrl = get(specialOffer, "ui.pictoUrl");

	const intl = useIntl();

	const negocatedSuffix = intl.formatMessage(
		{ id: "search.engine.negociated.with.offer" },
		{ offer: offerWithTransportLabel }
	);
	const negotiatedIcons = NEGOTIATED_ICONS(sizeIcon);

	let labelToDisplay = label;

	if (isTransportOnly && type !== NEGOCIATED_TYPES.SPECIAL_OFFER) {
		labelToDisplay = `${label} (${negocatedSuffix})`;
	}

	let picto = <CheckIcon size={sizeIcon} />;
	if (type === NEGOCIATED_TYPES.SPECIAL_OFFER && specialOfferPictoUrl) {
		picto = <img src={specialOfferPictoUrl} width={20} height={20} />;
	} else if (type && negotiatedIcons[type]) {
		picto = negotiatedIcons[type];
	}

	const negoClass = classNames("negociated-item", {
		"negociated-item--other": !type || type === NEGOCIATED_TYPES.OTHER,
		"negociated-item--special-offer": type === NEGOCIATED_TYPES.SPECIAL_OFFER,
	});

	return (
		<Fragment>
			<div
				className={negoClass}
				style={{ color: type === NEGOCIATED_TYPES.SPECIAL_OFFER && specialOfferTitleColor }}
			>
				<span className="negociated-item__picto">{picto}</span>
				<div className="negociated-item__main">
					{enableLineClamp ? (
						<LineClamp
							lineClampFullText={lineClampFullText}
							enableShowMore={true}
							lineCount={2}
							text={
								<span
									className="negociated-item__label"
									dangerouslySetInnerHTML={{
										__html: htmlSanitize(labelToDisplay),
									}}
								/>
							}
						/>
					) : (
						<>
							<span
								className="negociated-item__label"
								dangerouslySetInnerHTML={{
									__html: htmlSanitize(labelToDisplay),
								}}
							/>
							{labelPrefix}
						</>
					)}
				</div>
			</div>
			{type === NEGOCIATED_TYPES.SPECIAL_OFFER && (
				<span
					className="negociated-item__special-offer-details"
					style={{ color: specialOfferTitleColor }}
					dangerouslySetInnerHTML={{ __html: htmlSanitize(specialOffer.details) }}
				/>
			)}
		</Fragment>
	);
};

NegociatedItem.propTypes = {
	type: PropTypes.string,
	offerWithTransportLabel: PropTypes.string,
	label: PropTypes.string,
	labelPrefix: PropTypes.element,
	isTransportOnly: PropTypes.bool,
	specialOffer: PropTypes.object,
	lineClampFullText: PropTypes.object,
	enableLineClamp: PropTypes.bool,
	sizeIcon: PropTypes.oneOf(Object.values(ICON_SIZES)),
};

NegociatedItem.defaultProps = {
	specialOffer: {},
};

export default memo(NegociatedItem);
